import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Responsive, WidthProvider } from "react-grid-layout";
import { FormattedMessage } from "react-intl";
import BaseWidget from "../BaseWidget";
import { rendererFactory } from "../renderers/rendererFactory";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import Loader from "@amzn/meridian/loader";
import {
  generateLayouts,
  buildDashboardConfig,
  handleWidgetScrollToFocus,
} from "./DashboardUtilities";
import Row from "@amzn/meridian/row";
import { updateDashboardConfigLayout } from "../thunks/DashboardConfigurationDispatcher";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import {
  NO_WIDGETS_CONFIGURED,
  ADD_WIDGETS_TO_START,
} from "../../constants/Constants";

const ResponsiveGridLayout = WidthProvider(Responsive);

export default function Dashboard({
  stationType,
  isPriorityDashboard = false,
  pageSelected,
  viewTypeSelected,
}) {
  const dispatch = useDispatch();
  const renderer = rendererFactory(stationType);
  const [layouts, setLayouts] = useState(null);
  const widgetRefs = useRef({});
  const priorityDashboardConfig = useSelector(
    (state) => state.priorityDashboardConfig,
  );
  const unSavedDashboardConfig = useSelector(
    (state) => state.unSavedDashboardConfig,
  );
  const metricData = useSelector((state) => state.metricData);
  const lastAddedWidgetId = useSelector(
    (state) => state.unSavedDashboardConfig.lastAddedWidgetId,
  );
  const selectedRoles = useSelector((state) => state.selectedRoles);

  const dashboardConfig = buildDashboardConfig(
    isPriorityDashboard,
    priorityDashboardConfig,
    unSavedDashboardConfig,
    metricData,
    selectedRoles,
  );

  const isEditing = useSelector((state) => state.isEditMode);

  useEffect(() => {
    setLayouts(generateLayouts(dashboardConfig, isPriorityDashboard));
  }, [
    dashboardConfig.widgets && dashboardConfig.widgets.length,
    isPriorityDashboard,
  ]);

  // Scroll to newly added widget with edge case handling
  useEffect(() => {
    const cleanup = handleWidgetScrollToFocus(
      lastAddedWidgetId,
      layouts,
      isPriorityDashboard,
      widgetRefs,
      dispatch,
    );

    return cleanup;
  }, [lastAddedWidgetId, layouts, isPriorityDashboard, dispatch]);

  // When layout changes update the unsavedDashboard state
  const onLayoutChange = (layout, layouts) => {
    if (!isPriorityDashboard) {
      dispatch(updateDashboardConfigLayout(dashboardConfig, layout, layouts));
    }
  };

  // If dashboard config is loading show the loader
  if (dashboardConfig.loading || dashboardConfig.loading == null) {
    return (
      <Row alignmentVertical={"center"} alignmentHorizontal={"center"}>
        <Loader />
      </Row>
    );
  }

  // If no widgets are configured, show empty state
  if (
    (!dashboardConfig.widgets || dashboardConfig.widgets.length === 0) &&
    !isPriorityDashboard
  ) {
    return (
      <Row alignmentVertical={"center"} alignmentHorizontal={"center"}>
        <Column
          alignmentHorizontal="center"
          alignmentVertical={"center"}
          spacing={"100"}
        >
          <Text type="h100">
            <FormattedMessage
              id="mySCC.dashboard.noWidgets"
              defaultMessage={NO_WIDGETS_CONFIGURED}
            />
          </Text>
          <Text>
            <FormattedMessage
              id="mySCC.dashboard.addWidgets"
              defaultMessage={ADD_WIDGETS_TO_START}
            />
          </Text>
        </Column>
      </Row>
    );
  }

  if (!layouts) {
    return <div></div>;
  }

  return (
    <React.Fragment>
      {/* Responsive grid layout for widgets */}
      {/* Breakpoints from meridian: https://meridian.a2z.com/fundamentals/layout */}
      <ResponsiveGridLayout
        className="layout"
        layouts={layouts}
        breakpoints={{
          lg: 960,
          md: 720,
          sm: 480,
        }}
        cols={{ lg: 12, md: 8, sm: 4 }}
        rowHeight={50}
        isDraggable={isPriorityDashboard ? false : isEditing}
        isResizable={isPriorityDashboard ? false : isEditing}
        onLayoutChange={onLayoutChange}
      >
        {(dashboardConfig.widgets || []).map((widgetSpec, i) => (
          <div key={widgetSpec.widgetId}>
            <BaseWidget
              ref={(el) => {
                if (el) {
                  widgetRefs.current[widgetSpec.widgetId] = el;
                } else {
                  delete widgetRefs.current[widgetSpec.widgetId];
                }
              }}
              widgetSpec={widgetSpec}
              renderer={renderer}
              isPriorityDashboard={isPriorityDashboard}
              stationType={stationType}
              pageSelected={pageSelected}
              viewTypeSelected={viewTypeSelected}
            />
          </div>
        ))}
      </ResponsiveGridLayout>
    </React.Fragment>
  );
}



// WEBPACK FOOTER //
// ./src/components/mySCC/common/Dashboard/Dashboard.js